import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Linkify from "react-linkify";
import { ActivitySeatDetailContainerStyled } from "./styled";
import { userService } from "apiServices";
import { ROUTE_PATH } from "utils/constants/routePath";
import { IMG_PATH } from "utils/constants/imgPath";
import {
  SEAT_BOOKING_CLOSED_TEXT,
  SEAT_BOOKING_ERROR_CODE,
} from "utils/functions/seatBooking";
import { Buttons, Cards, Displays, Headers, Navigations } from "components";
import ImageSmooth from "components/Displays/ImageSmooth";

// หน้ากิจกรรมของกิจกรรมจองที่นั่ง (S0/S7/S8 รวมหน้าเดียว) แยกจากหน้ากิจกรรมเดิมทั้งหมด
// ปุ่ม Booking Seat อยู่เสมอ (จองครั้งแรก/จองเพิ่ม/ทำต่อ draft) การ์ดใบจองที่ส่งแล้วอยู่ด้านล่าง
const ActivitySeatDetailContainer = ({ activity }) => {
  const { t, i18n } = useTranslation(["seat", "common"]);
  const navigate = useNavigate();
  const [_isLoading, _setIsLoading] = useState(true);
  const [_info, _setInfo] = useState(null);
  const [_errorCode, _setErrorCode] = useState(null);
  const [_bookings, _setBookings] = useState([]);
  const [_serverOffset, _setServerOffset] = useState(0);
  const [_now, _setNow] = useState(Date.now());

  const _fetchData = useCallback(async () => {
    const [resInfo, resBookings] = await Promise.all([
      userService.GET_SEAT_BOOKING_INFO(activity._id),
      userService.GET_MY_SEAT_BOOKINGS(activity._id),
    ]);
    if (resInfo && resInfo.status === 200) {
      _setInfo(resInfo.data);
      _setServerOffset(new Date(resInfo.data.server_now).getTime() - Date.now());
      _setErrorCode(null);
    } else {
      _setErrorCode(SEAT_BOOKING_ERROR_CODE(resInfo) || "UNKNOWN");
    }
    if (resBookings && resBookings.status === 200) _setBookings(resBookings.data);
    _setIsLoading(false);
  }, [activity._id]);

  useEffect(() => {
    _fetchData();
  }, [_fetchData]);

  useEffect(() => {
    const timer = setInterval(() => _setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const draft = _info && _info.my_draft;
  const remainingMs = draft ? new Date(draft.expires_at).getTime() - (_now + _serverOffset) : 0;
  const hasDraft = draft && remainingMs > 0;
  const countdownText = `${String(Math.floor(Math.max(0, remainingMs) / 60000)).padStart(2, "0")}:${String(
    Math.floor((Math.max(0, remainingMs) % 60000) / 1000)
  ).padStart(2, "0")}`;

  const _goBooking = () => navigate(`${ROUTE_PATH.ACTIVITIES}/${activity._id}/seat-booking`);

  const renderBookingButton = () => {
    if (_errorCode) {
      return (
        <div className="notice_text">
          {t(
            _errorCode === "MEMBER_TYPE_NOT_ALLOWED"
              ? "Seat booking is available for individual members only. Please switch to your personal account."
              : "Something went wrong. Please try again.",
            { ns: "seat" }
          )}
        </div>
      );
    }
    if (hasDraft) {
      return (
        <Buttons.BgStandard
          height="48px"
          label={t("Continue booking ({{time}} left)", { ns: "seat", time: countdownText })}
          onClick={_goBooking}
        />
      );
    }
    if (_info && _info.can_book) {
      return <Buttons.BgStandard height="48px" label={t("Booking seat", { ns: "seat" })} onClick={_goBooking} />;
    }
    return (
      <>
        <Buttons.BgStandard height="48px" label={t("Booking seat", { ns: "seat" })} disabled />
        <div className="notice_text">
          {SEAT_BOOKING_CLOSED_TEXT(t, _info)}
        </div>
      </>
    );
  };

  return (
    <ActivitySeatDetailContainerStyled>
      <div className="breadcrumb_layout">
        <Navigations.Breadcrumb
          data={[
            { name: t("Home", { ns: "common" }), path: ROUTE_PATH.HOME },
            { name: t("Activity News", { ns: "common" }), path: ROUTE_PATH.ACTIVITIES },
          ]}
        />
      </div>
      <div className="seat_detail_container">
        <Headers.NewPageHeader title={"ACTIVITY : Booking Seat"} sub_title={t("Activity: Seat booking", { ns: "seat" })} />
        <div className="activity_wrap">
          <ImageSmooth
            alt="activity"
            src={activity.img ? IMG_PATH + activity.img : "/assets/images/mockups/activities-1.jpg"}
            aspectRatio="1 / 1"
          />
        </div>
        <div className="activity_title">{i18n.language === "en" ? activity.name_en : activity.name_th}</div>
        <div className="activity_desc">
          <Linkify
            componentDecorator={(decoratedHref, decoratedText, key) => (
              <a target="blank" href={decoratedHref} key={key}>
                {decoratedText}
              </a>
            )}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: i18n.language === "en" ? activity.description_en : activity.description_th,
              }}
            />
          </Linkify>
        </div>
        {/* Picture2 แสดงใต้รายละเอียดแบบเดียวกับหน้ากิจกรรมเดิม */}
        {activity.img2 && (
          <div className="activity_img2">
            <ImageSmooth alt="activity" src={IMG_PATH + activity.img2} aspectRatio="1 / 1" />
          </div>
        )}

        {_isLoading ? (
          <Displays.Loading />
        ) : (
          <>
            <div className="booking_btn">{renderBookingButton()}</div>
            {_bookings.length > 0 && (
              <div className="my_bookings">
                <div className="section_title">{t("List of Participants", { ns: "seat" })}</div>
                {_bookings.map((b, i) => (
                  <Cards.SeatBookingItem key={b.booking_id} index={i} booking={b} />
                ))}
              </div>
            )}
          </>
        )}
        <div className="back_btn">
          <Buttons.BgStandard
            theme_white_purple
            height="48px"
            label={t("Back", { ns: "seat" })}
            onClick={() => navigate(ROUTE_PATH.ACTIVITIES)}
          />
        </div>
      </div>
    </ActivitySeatDetailContainerStyled>
  );
};

export default ActivitySeatDetailContainer;
